import { FormEvent, useEffect, useMemo, useState } from 'react';
import { FolderPlus, Tags, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { categoryApi } from '@/services/api';
import { ContentCategory, ContentCategoryType } from '@/types';

const TYPE_OPTIONS: Array<{ value: ContentCategoryType; label: string }> = [
  { value: 'course', label: 'Course List' },
  { value: 'sports', label: 'Sports' },
  { value: 'event', label: 'Events' },
];

const typeLabel = (type: ContentCategoryType) =>
  TYPE_OPTIONS.find((option) => option.value === type)?.label || type;

export const AdminCategoriesPage = () => {
  const [categories, setCategories] = useState<ContentCategory[]>([]);
  const [selectedType, setSelectedType] = useState<ContentCategoryType>('course');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [deletingId, setDeletingId] = useState('');
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const loadCategories = async (signal?: AbortSignal) => {
    const data = await categoryApi.getCategories({ signal });
    setCategories(data);
  };

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    loadCategories(controller.signal)
      .catch((requestError: Error) => {
        if (requestError.name !== 'AbortError') {
          setError(requestError.message || 'Categories could not be loaded.');
        }
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, []);

  const visibleCategories = useMemo(
    () => categories.filter((category) => category.type === selectedType),
    [categories, selectedType]
  );

  const handleCreate = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const normalizedName = name.trim();
    setError('');
    setSuccessMessage('');

    if (!normalizedName) {
      setError('Enter a category name.');
      return;
    }

    setIsSaving(true);
    try {
      const category = await categoryApi.createCategory({ name: normalizedName, type: selectedType });
      setCategories((current) =>
        [...current, category].sort((left, right) => left.name.localeCompare(right.name))
      );
      setName('');
      setSuccessMessage(`${category.name} was added to ${typeLabel(category.type)}.`);
    } catch (requestError) {
      setError((requestError as Error).message || 'Category could not be created.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (category: ContentCategory) => {
    if (!window.confirm(`Delete the “${category.name}” category?`)) return;

    setError('');
    setSuccessMessage('');
    setDeletingId(category.id);
    try {
      await categoryApi.deleteCategory(category.id);
      setCategories((current) => current.filter((item) => item.id !== category.id));
      setSuccessMessage(`${category.name} was deleted.`);
    } catch (requestError) {
      setError((requestError as Error).message || 'Category could not be deleted.');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div className="max-w-5xl space-y-8">
      <div>
        <h1 className="page-heading">Categories</h1>
        <p className="page-subtitle">
          Create reusable categories for courses, sports listings, events, and workshops.
        </p>
      </div>

      <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
        <div className="flex items-start gap-3">
          <div className="rounded-xl bg-[rgb(var(--accent-solid))/0.10] p-3 text-[rgb(var(--accent-solid))]">
            <FolderPlus className="h-5 w-5" />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-slate-950">Create a category</h2>
            <p className="mt-1 text-sm text-slate-600">Choose where it belongs, then enter its display name.</p>
          </div>
        </div>

        <form onSubmit={handleCreate} className="mt-6 grid gap-5 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.4fr)_auto] md:items-end">
          <div>
            <label className="form-label">Category For</label>
            <select
              className="form-control min-h-11"
              value={selectedType}
              onChange={(event) => {
                setSelectedType(event.target.value as ContentCategoryType);
                setError('');
                setSuccessMessage('');
              }}
            >
              {TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          <Input
            label="Category Name"
            value={name}
            maxLength={80}
            placeholder="e.g., Technology, Cricket, Cultural"
            onChange={(event) => setName(event.target.value)}
          />

          <Button type="submit" disabled={isSaving}>
            <FolderPlus className="h-4 w-4" />
            {isSaving ? 'Creating...' : 'Create Category'}
          </Button>
        </form>

        {error ? <p className="status-message mt-5 border-red-100 bg-red-50 text-red-700">{error}</p> : null}
        {successMessage ? (
          <p className="status-message mt-5 border-emerald-100 bg-emerald-50 text-emerald-700">{successMessage}</p>
        ) : null}
      </Card>

      <Card className="rounded-[1.75rem] border-white/70 bg-white/94 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-950">{typeLabel(selectedType)} Categories</h2>
            <p className="mt-1 text-sm text-slate-600">
              {visibleCategories.length} {visibleCategories.length === 1 ? 'category' : 'categories'} available
            </p>
          </div>
          <div className="flex flex-wrap gap-2" aria-label="Filter category type">
            {TYPE_OPTIONS.map((option) => (
              <Button
                key={option.value}
                type="button"
                size="sm"
                variant={selectedType === option.value ? 'primary' : 'secondary'}
                onClick={() => setSelectedType(option.value)}
              >
                {option.label}
              </Button>
            ))}
          </div>
        </div>

        <div className="mt-6">
          {isLoading ? (
            <p className="rounded-2xl bg-slate-50 p-6 text-center text-sm text-slate-600">Loading categories...</p>
          ) : visibleCategories.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50/80 p-8 text-center">
              <Tags className="mx-auto h-7 w-7 text-slate-400" />
              <p className="mt-3 text-sm font-medium text-slate-700">No {typeLabel(selectedType).toLowerCase()} categories yet.</p>
            </div>
          ) : (
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {visibleCategories.map((category) => (
                <div
                  key={category.id}
                  className="flex items-center justify-between gap-3 rounded-2xl border border-slate-200 bg-slate-50/70 px-4 py-3"
                >
                  <div className="min-w-0">
                    <p className="truncate font-semibold text-slate-900">{category.name}</p>
                    <p className="mt-0.5 text-xs font-medium uppercase tracking-[0.12em] text-slate-500">
                      {typeLabel(category.type)}
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="shrink-0"
                    aria-label={`Delete ${category.name}`}
                    disabled={deletingId === category.id}
                    onClick={() => void handleDelete(category)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
